import {
  AnchorIcon,
  BadgeCheckIcon,
  BanknoteIcon,
  BriefcaseBusinessIcon,
  CarIcon,
  ClipboardCheckIcon,
  ClockIcon,
  FactoryIcon,
  HeartIcon,
  LanguagesIcon,
  NutIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  WrenchIcon,
  type LucideIcon,
} from "lucide-react";

import {
  CallStructuredSummaryCatalog,
  CallStructuredSummaryFieldKey,
  CallStructuredSummaryGroup,
} from "@/types";

// Presentation for each field guac can return. Guac owns the extraction, we own
// the label, the icon and the order, so reordering or relabelling a row never
// needs a backend change. A key missing here simply doesn't render.
//
// Two catalogs share this file. The Bridgetown pilot keeps its own field list
// untouched. Every other job uses the generic one. A summary says which it was
// built against, and the breakdown picks the matching groups and labels.
export const STEVIE_FIELD_LABELS: Record<
  CallStructuredSummaryFieldKey,
  string
> = {
  // Bridgetown
  [CallStructuredSummaryFieldKey.NUT_ALLERGY]: "Nut allergy",
  [CallStructuredSummaryFieldKey.ENGLISH_PROFICIENCY]: "English",
  [CallStructuredSummaryFieldKey.LONGEST_TENURE]: "Longest tenure",
  [CallStructuredSummaryFieldKey.EXPECTED_PAY]: "Expected pay",
  [CallStructuredSummaryFieldKey.BEST_FIT]: "Best fit",
  [CallStructuredSummaryFieldKey.RELEVANT_EXPERIENCE_HIGHLIGHTS]:
    "Relevant experience",
  [CallStructuredSummaryFieldKey.WHY_THEYLL_LIKE_IT]: "Why they'll like it",
  [CallStructuredSummaryFieldKey.EVIDENCE_SHOW_UP_AND_STAY]:
    "Evidence they'll show up + stay",
  [CallStructuredSummaryFieldKey.RISKS]: "Risks",
  [CallStructuredSummaryFieldKey.PRODUCTION_EQUIPMENT]: "Production equipment",
  [CallStructuredSummaryFieldKey.INGREDIENT_HANDLING]: "Ingredient handling",
  [CallStructuredSummaryFieldKey.PACKAGING]: "Packaging",
  [CallStructuredSummaryFieldKey.MATERIAL_HANDLING]: "Material handling",
  [CallStructuredSummaryFieldKey.SANITATION]: "Sanitation",
  [CallStructuredSummaryFieldKey.GMP_SOP_EXPERIENCE]: "SOP / GMP experience",
  [CallStructuredSummaryFieldKey.TROUBLESHOOTING]: "Troubleshooting",
  [CallStructuredSummaryFieldKey.PRODUCTION_DOCUMENTATION]:
    "Production documentation",
  [CallStructuredSummaryFieldKey.PREFERRED_SHIFT]: "Preferred shift",
  [CallStructuredSummaryFieldKey.ELIGIBLE_SHIFTS]: "Eligible shifts",
  [CallStructuredSummaryFieldKey.CONFIRMED_SCHEDULE]: "Confirmed schedule",
  [CallStructuredSummaryFieldKey.ATTENDANCE]: "Attendance",
  [CallStructuredSummaryFieldKey.TRANSPORTATION]: "Transportation",
  [CallStructuredSummaryFieldKey.COMMUTE_TIME]: "Commute time",
  [CallStructuredSummaryFieldKey.REASON_FOR_LEAVING_LAST_ROLE]:
    "Reason for leaving last role",
  [CallStructuredSummaryFieldKey.INTENT]: "Intent",
  // Generic
  [CallStructuredSummaryFieldKey.PAY]: "Pay",
  [CallStructuredSummaryFieldKey.COMMUTE]: "Commute",
  [CallStructuredSummaryFieldKey.SHIFT]: "Shift",
  [CallStructuredSummaryFieldKey.DRUG_AND_BACKGROUND]: "Drug & background",
  [CallStructuredSummaryFieldKey.TRADE_SKILLS]: "Trade-specific skills",
  [CallStructuredSummaryFieldKey.EQUIPMENT_TOOLS_SYSTEMS]:
    "Equipment, tools & systems",
  [CallStructuredSummaryFieldKey.MODELS_BRANDS]: "Models & brands",
  [CallStructuredSummaryFieldKey.PROCESS_OWNERSHIP]: "Process ownership",
  [CallStructuredSummaryFieldKey.COMPLEXITY_INDEPENDENCE]:
    "Complexity & independence",
  [CallStructuredSummaryFieldKey.QUALITY_PRECISION]: "Quality & precision",
};

// Labels that read differently depending on the catalog. The pilot names the
// customer where the generic list cannot.
const STEVIE_FIELD_LABEL_OVERRIDES: Record<
  CallStructuredSummaryCatalog,
  Partial<Record<CallStructuredSummaryFieldKey, string>>
> = {
  [CallStructuredSummaryCatalog.BRIDGETOWN]: {
    [CallStructuredSummaryFieldKey.WHY_THEYLL_LIKE_IT]:
      "Why they'll like Bridgetown",
  },
  [CallStructuredSummaryCatalog.GENERIC]: {},
};

export const stevieFieldLabel = (
  catalog: CallStructuredSummaryCatalog,
  key: CallStructuredSummaryFieldKey,
): string =>
  STEVIE_FIELD_LABEL_OVERRIDES[catalog][key] ?? STEVIE_FIELD_LABELS[key];

// Only the cards and the highlight rows carry their own icon. The grouped rows
// below them sit under a single group icon instead.
export const STEVIE_FIELD_ICONS: Partial<
  Record<CallStructuredSummaryFieldKey, LucideIcon>
> = {
  [CallStructuredSummaryFieldKey.NUT_ALLERGY]: NutIcon,
  [CallStructuredSummaryFieldKey.ENGLISH_PROFICIENCY]: LanguagesIcon,
  [CallStructuredSummaryFieldKey.LONGEST_TENURE]: ClockIcon,
  [CallStructuredSummaryFieldKey.EXPECTED_PAY]: BanknoteIcon,
  [CallStructuredSummaryFieldKey.BEST_FIT]: BadgeCheckIcon,
  [CallStructuredSummaryFieldKey.RELEVANT_EXPERIENCE_HIGHLIGHTS]:
    BriefcaseBusinessIcon,
  [CallStructuredSummaryFieldKey.WHY_THEYLL_LIKE_IT]: HeartIcon,
  [CallStructuredSummaryFieldKey.EVIDENCE_SHOW_UP_AND_STAY]: ShieldCheckIcon,
  [CallStructuredSummaryFieldKey.RISKS]: ShieldAlertIcon,
  [CallStructuredSummaryFieldKey.PAY]: BanknoteIcon,
  [CallStructuredSummaryFieldKey.COMMUTE]: CarIcon,
  [CallStructuredSummaryFieldKey.SHIFT]: ClockIcon,
  [CallStructuredSummaryFieldKey.DRUG_AND_BACKGROUND]: ClipboardCheckIcon,
};

type StevieRowGroup = {
  group: CallStructuredSummaryGroup;
  Icon: LucideIcon;
  title: string;
};

// The grouped rows, in the order they render, per catalog. Quick logistics and
// highlights get their own layouts, so they aren't listed here.
export const STEVIE_ROW_GROUPS_BY_CATALOG: Record<
  CallStructuredSummaryCatalog,
  StevieRowGroup[]
> = {
  [CallStructuredSummaryCatalog.BRIDGETOWN]: [
    {
      group: CallStructuredSummaryGroup.HANDS_ON_EXPERIENCE,
      Icon: FactoryIcon,
      title: "Hands-on experience",
    },
    {
      group: CallStructuredSummaryGroup.SCHEDULE_FIT,
      Icon: ClockIcon,
      title: "Schedule fit",
    },
    {
      group: CallStructuredSummaryGroup.RELIABILITY,
      Icon: CarIcon,
      title: "Reliability",
    },
    {
      group: CallStructuredSummaryGroup.RETENTION,
      Icon: AnchorIcon,
      title: "Retention",
    },
  ],
  [CallStructuredSummaryCatalog.GENERIC]: [
    {
      group: CallStructuredSummaryGroup.HANDS_ON_EXPERIENCE,
      Icon: WrenchIcon,
      title: "Hands-on experience",
    },
  ],
};

// Which fields belong to which group, and their order within it, per catalog.
// Guac sends the group on each field too, but keeping the mapping here means
// the FE can order and lay out rows without trusting the payload's ordering.
export const STEVIE_FIELD_GROUPS_BY_CATALOG: Record<
  CallStructuredSummaryCatalog,
  Partial<Record<CallStructuredSummaryGroup, CallStructuredSummaryFieldKey[]>>
> = {
  [CallStructuredSummaryCatalog.BRIDGETOWN]: {
    [CallStructuredSummaryGroup.QUICK_LOGISTICS]: [
      CallStructuredSummaryFieldKey.NUT_ALLERGY,
      CallStructuredSummaryFieldKey.ENGLISH_PROFICIENCY,
      CallStructuredSummaryFieldKey.LONGEST_TENURE,
      CallStructuredSummaryFieldKey.EXPECTED_PAY,
    ],
    [CallStructuredSummaryGroup.HIGHLIGHT]: [
      CallStructuredSummaryFieldKey.BEST_FIT,
      CallStructuredSummaryFieldKey.RELEVANT_EXPERIENCE_HIGHLIGHTS,
      CallStructuredSummaryFieldKey.WHY_THEYLL_LIKE_IT,
      CallStructuredSummaryFieldKey.EVIDENCE_SHOW_UP_AND_STAY,
      CallStructuredSummaryFieldKey.RISKS,
    ],
    [CallStructuredSummaryGroup.HANDS_ON_EXPERIENCE]: [
      CallStructuredSummaryFieldKey.PRODUCTION_EQUIPMENT,
      CallStructuredSummaryFieldKey.INGREDIENT_HANDLING,
      CallStructuredSummaryFieldKey.PACKAGING,
      CallStructuredSummaryFieldKey.MATERIAL_HANDLING,
      CallStructuredSummaryFieldKey.SANITATION,
      CallStructuredSummaryFieldKey.GMP_SOP_EXPERIENCE,
      CallStructuredSummaryFieldKey.TROUBLESHOOTING,
      CallStructuredSummaryFieldKey.PRODUCTION_DOCUMENTATION,
    ],
    [CallStructuredSummaryGroup.SCHEDULE_FIT]: [
      CallStructuredSummaryFieldKey.PREFERRED_SHIFT,
      CallStructuredSummaryFieldKey.ELIGIBLE_SHIFTS,
      CallStructuredSummaryFieldKey.CONFIRMED_SCHEDULE,
    ],
    [CallStructuredSummaryGroup.RELIABILITY]: [
      CallStructuredSummaryFieldKey.ATTENDANCE,
      CallStructuredSummaryFieldKey.TRANSPORTATION,
      CallStructuredSummaryFieldKey.COMMUTE_TIME,
    ],
    [CallStructuredSummaryGroup.RETENTION]: [
      CallStructuredSummaryFieldKey.REASON_FOR_LEAVING_LAST_ROLE,
      CallStructuredSummaryFieldKey.INTENT,
    ],
  },
  [CallStructuredSummaryCatalog.GENERIC]: {
    [CallStructuredSummaryGroup.QUICK_LOGISTICS]: [
      CallStructuredSummaryFieldKey.PAY,
      CallStructuredSummaryFieldKey.COMMUTE,
      CallStructuredSummaryFieldKey.SHIFT,
      CallStructuredSummaryFieldKey.DRUG_AND_BACKGROUND,
    ],
    [CallStructuredSummaryGroup.HIGHLIGHT]: [
      CallStructuredSummaryFieldKey.RELEVANT_EXPERIENCE_HIGHLIGHTS,
      CallStructuredSummaryFieldKey.WHY_THEYLL_LIKE_IT,
      CallStructuredSummaryFieldKey.EVIDENCE_SHOW_UP_AND_STAY,
      CallStructuredSummaryFieldKey.RISKS,
    ],
    [CallStructuredSummaryGroup.HANDS_ON_EXPERIENCE]: [
      CallStructuredSummaryFieldKey.TRADE_SKILLS,
      CallStructuredSummaryFieldKey.EQUIPMENT_TOOLS_SYSTEMS,
      CallStructuredSummaryFieldKey.MODELS_BRANDS,
      CallStructuredSummaryFieldKey.PROCESS_OWNERSHIP,
      CallStructuredSummaryFieldKey.COMPLEXITY_INDEPENDENCE,
      CallStructuredSummaryFieldKey.QUALITY_PRECISION,
    ],
  },
};
